// @flow
import React, {Component} from 'react';
import {StyleSheet, View} from 'react-native';
import type {NavigationScreenProp} from '@react-navigation/compat';
import {Field, propTypes} from 'redux-form';
import {FullButton, HoshiInput, Checkbox, GradientPage} from '../../../components/common';
import {PageTitle} from '../../../components/signUp';
import I18n from '../../../lib/i18n';
import pageStyle from '../styles';
import {getStateOrProvinceFromPostCode} from './lib/getInfosFromPostCode';
import {replaceSpecialCharacters} from './lib/replaceSpecialCharacters';

export default class Address extends Component<Props> {
  static propTypes = {
    ...propTypes,
  };

  styles = getStyles();
  street1Input: any;
  street2Input: any;
  postCodeInput: any;
  cityInput: any;
  stateOrProvinceInput: any;

  componentDidUpdate(prevProps: Props) {
    const addressFields = ['street1', 'city', 'postCode', 'stateOrProvince'];

    addressFields.forEach((addressField: string) => {
      if (
        prevProps.address[addressField] !== this.props.address[addressField]
      ) {
        this.props.changeFormField(
          addressField,
          this.props.address[addressField],
        );
      }
    });

    if (
      prevProps.inferStateOrProvinceFromPostCode &&
      prevProps.postCode !== this.props.postCode
    ) {
      const inferredStateOrProvince = getStateOrProvinceFromPostCode(
        this.props.postCode,
      );
      this.props.changeFormField('stateOrProvince', inferredStateOrProvince);
    }
  }

  focusOnHouseNumber = () =>
    this.focusOnHouseNumber &&
    this.focusOnHouseNumber.getRenderedComponent().focus();
  focusOnStreet1Input = () =>
    this.street1Input && this.street1Input.getRenderedComponent().focus();
  focusOnStreet2Input = () =>
    this.street2Input && this.street2Input.getRenderedComponent().focus();
  focusOnPostCodeInput = () =>
    this.postCodeInput && this.postCodeInput.getRenderedComponent().focus();
  focusOnCityInput = () =>
    this.cityInput && this.cityInput.getRenderedComponent().focus();
  focusOnStateOrProvinceInput = () =>
    this.stateOrProvinceInput &&
    this.stateOrProvinceInput.getRenderedComponent().focus();

  render() {
    const styles = this.styles;
    const streetAndCityMaxLength = 38;
    const postCodeMaxLength = 5;

    // Theses props come from redux-form. We can know the form status to adjust the confirm button
    const {invalid, submitting, eSimSelected} = this.props;
    return (
        <View style={styles.container}>

          {this.props.title && (
            <PageTitle title={this.props.title} style={pageStyle.title} />
          )}

          <View style={[pageStyle.form, styles.form]}>
            <View>
              {this.props.displayFullName && (
                <Field
                  name="fullName"
                  component={HoshiInput}
                  placeholder={I18n.t('SignUp.DeliveryAddress.fullName')}
                  returnKeyType="next"
                  style={styles.inputContainer}
                  onSubmitEditing={
                    this.props.displayHouseNumber
                      ? this.focusOnHouseNumber
                      : this.focusOnStreet1Input
                  }
                  disabled={this.props.isFullNameDisabled}
                />
              )}
              {this.props.displayHouseNumber && (
                <Field
                  name="houseNumber"
                  component={HoshiInput}
                  placeholder={I18n.t('SignUp.DeliveryAddress.form.houseNumber')}
                  returnKeyType="next"
                  style={styles.inputContainer}
                  onSubmitEditing={this.focusOnStreet1Input}
                />
              )}
              <Field
                ref={ref => (this.street1Input = ref)}
                forwardRef
                name="street1"
                component={HoshiInput}
                placeholder={I18n.t('SignUp.DeliveryAddress.form.street1')}
                returnKeyType="next"
                style={styles.inputContainer}
                onSubmitEditing={this.focusOnStreet2Input}
                maxLength={streetAndCityMaxLength}
                testID={'addressInfoStreet1'}
                normalize={replaceSpecialCharacters}
              />
              <Field
                ref={ref => (this.street2Input = ref)}
                forwardRef
                name="street2"
                component={HoshiInput}
                placeholder={I18n.t('SignUp.DeliveryAddress.form.street2')}
                returnKeyType="next"
                style={styles.inputContainer}
                onSubmitEditing={this.focusOnPostCodeInput}
                maxLength={streetAndCityMaxLength}
                testID={'addressInfoStreet2'}
                normalize={replaceSpecialCharacters}
              />
              <Field
                ref={ref => (this.postCodeInput = ref)}
                forwardRef
                style={[styles.postCode, styles.inputContainer]}
                name="postCode"
                component={HoshiInput}
                placeholder={I18n.t('SignUp.DeliveryAddress.form.postCode')}
                returnKeyType="next"
                errorNumberOfLine={2}
                onSubmitEditing={this.focusOnCityInput}
                errorStyle={styles.postCode}
                maxLength={postCodeMaxLength}
                testID={'addressInfoPostCode'}
              />
              <Field
                ref={ref => (this.cityInput = ref)}
                forwardRef
                style={styles.inputContainer}
                name="city"
                component={HoshiInput}
                placeholder={I18n.t('SignUp.DeliveryAddress.form.city')}
                returnKeyType="next"
                onSubmitEditing={this.focusOnStateOrProvinceInput}
                maxLength={streetAndCityMaxLength}
                testID={'addressInfoCity'}
              />
              <Field
                ref={ref => (this.stateOrProvinceInput = ref)}
                forwardRef
                name="stateOrProvince"
                component={HoshiInput}
                placeholder={I18n.t('SignUp.DeliveryAddress.form.region')}
                returnKeyType="go"
                style={styles.inputContainer}
                disabled={this.props.inferStateOrProvinceFromPostCode}
                onSubmitEditing={this.props.handleSubmit}
                testID={'addressInfoStateOrProvince'}
              />
              {this.props.displayCheckboxUseSameAddresses && !eSimSelected && (
                <Field
                  name="useSameAddressForDelivery"
                  component={({input: {value, onChange}}) => (
                    <Checkbox
                      text={I18n.t(
                        'SignUp.BillingAddress.useSameAddressForDelivery',
                      )}
                      style={[styles.inputContainer, styles.checkboxContainer]}
                      onPress={() => onChange(!value)}
                      isChecked={value}
                      lightColors
                    />
                  )}
                />
              )}
            </View>
            <FullButton
              onPress={this.props.handleSubmit}
              text={I18n.t('confirm')}
              isLoading={this.props.isLoading}
              testID={'addressConfirmButton'}
              disabled={invalid || submitting}
            />
          </View>
        </View>
    );
  }
}

export type BillingAddressFlaggedProps = {
  displayFullName: boolean,
  inferStateOrProvinceFromPostCode: boolean,
};

export type DeliveryAddressFlaggedProps = {
  inferStateOrProvinceFromPostCode: boolean,
};

type Props = NavigationScreenProp & {
  handleSubmit: Function,
  isLoading?: boolean,
  postCode: string,
  changeStateOrProvince: string => null,
} & (BillingAddressFlaggedProps | DeliveryAddressFlaggedProps) & {
    title?: string,
    displayCheckboxUseSameAddresses: boolean,
    displayHouseNumber: boolean,
  };

const getStyles = () =>
  StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
      maxWidth: 600,
      alignSelf: 'center',
      justifyContent: 'center',
    },
    form: {
      marginTop: 20,
    },
    inputContainer: {
      marginVertical: 15,
    },
    checkboxContainer: {
      marginBottom: 40,
    },
    cityContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    postCode: {
      width: 175,
    },
    searchButton: {
      marginBottom: 15,
    },
  });
